import { useCallback, useState } from 'react';
import Panel from '@/components/ui/Panel';
import { EcommPanelParams } from './hooks/useEcommData';
import EcommPanelLiveProgram from './EcommPanelLiveProgram';
import EcommPanelOnDemandSegment from './EcommPanelOnDemandSegment';
import EcommPanelOnDemandProgram from './EcommPanelOnDemandProgram';
import EcommPanelPublication from './EcommPanelPublication';
import { AnalyticsContext } from '@/analytics/constants';
import { useContentOptOut } from '@/hooks/PDP/useContentOptOut';
import PanelContentWithErrorBoundary from '@/components/blocks/ErrorFallback/PanelContentWithErrorBoundary';
import { useDarkModeContext } from '../PDP/DarkModeContext/useDarkModeContext';
import { PDPSliceSelectorProvider } from '@/hooks/shared/usePDPSliceSelector';
import { SliceSectionName } from '@/redux/slices/pdpSlice';

const renderContent = (
  params: EcommPanelParams,
  onClose: () => void,
  context: AnalyticsContext | undefined,
  setIsLoading?: (loading?: boolean) => void
): JSX.Element => {
  if (params.searchableType === 'liveprogram') {
    return (
      <EcommPanelLiveProgram
        params={params}
        onClose={onClose}
        context={context}
        setIsLoading={setIsLoading}
        itemListAnalyticsModel={params.itemListAnalyticsModel}
      />
    );
  }
  if (params.searchableType === 'ondemandprogram') {
    return (
      <EcommPanelOnDemandProgram
        params={params}
        onClose={onClose}
        context={context}
        setIsLoading={setIsLoading}
        itemListAnalyticsModel={params.itemListAnalyticsModel}
      />
    );
  }
  if (params.searchableType === 'ondemandsegment') {
    return (
      <EcommPanelOnDemandSegment
        params={params}
        onClose={onClose}
        context={context}
        setIsLoading={setIsLoading}
        itemListAnalyticsModel={params.itemListAnalyticsModel}
      />
    );
  }
  if (params.searchableType === 'book') {
    return (
      <EcommPanelPublication
        params={params}
        onClose={onClose}
        context={context}
        setIsLoading={setIsLoading}
        itemListAnalyticsModel={params.itemListAnalyticsModel}
      />
    );
  }
  return <></>;
};

export type EcommPanelProps = {
  params?: EcommPanelParams;
  onClose?: () => void;
  triggerButton?: JSX.Element;
  customPanelId?: string;
  context?: AnalyticsContext;
  onOpenChange?: (isOpen: boolean) => void;
};

const EcommPanel = ({
  params,
  onClose,
  triggerButton,
  customPanelId,
  context,
  onOpenChange
}: EcommPanelProps) => {
  useContentOptOut();
  const [isOpen, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const darkmodeContext = useDarkModeContext();
  const isDarkMode = darkmodeContext?.isDarkMode ?? false;

  const handleOnClose = useCallback(() => {
    if (triggerButton) {
      setOpen(false);
    } else {
      onClose?.();
    }
  }, [onClose, triggerButton]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);
      onOpenChange?.(isOpen);
    },
    [onOpenChange]
  );

  const setLoading = useCallback((loading?: boolean) => {
    setIsLoading(!!loading);
  }, []);

  return (
    <PDPSliceSelectorProvider sliceName={SliceSectionName.PANEL}>
      <Panel
        isOpen={triggerButton ? isOpen : params !== undefined}
        onOpenChange={triggerButton ? handleOpenChange : onClose}
        size="small"
        dataComponent="EcommPanel"
        theme={isDarkMode ? 'dark' : 'light'}
      >
        {triggerButton && <Panel.Trigger asChild>{triggerButton}</Panel.Trigger>}
        <Panel.Content side="right" hasHorizontalPadding={false} customPanelId={customPanelId}>
          <PanelContentWithErrorBoundary
            sectionClassNames="!mb-0 !h-3/4 lg:!p-0"
            containerClassNames="!self-end"
            isRetrying={isLoading}
          >
            <Panel.Body className={isDarkMode ? 'text-white' : 'text-black'}>
              {params ? renderContent(params, handleOnClose, context, setLoading) : undefined}
            </Panel.Body>
          </PanelContentWithErrorBoundary>
        </Panel.Content>
      </Panel>
    </PDPSliceSelectorProvider>
  );
};

export default EcommPanel;
